import { deleteField, doc, getDoc, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, db } from '../config/firebaseConfig';
import { normalizeEiaScriptDocument } from './eiaConfigResolver';
import recruitmentVideoMap from '../config/recruitmentVideoMap.json';
import { buildStatusPayload } from '../utils/interviewStatusModel';
import { caminhoDaEntrevista } from '../utils/caminhosConta';

const functions = getFunctions(app, 'southamerica-east1');

const RS_DEFAULT_SCRIPT_VERSION = 'rs-mvp-v1';
const RS_DEFAULT_INTRO_CLIP = 'intro';
const RS_DEFAULT_INTRO_TEXT =
  'Olá! Eu sou a Elza e vou conduzir sua entrevista de recrutamento e seleção. Vou fazer perguntas objetivas sobre sua experiência e seu perfil profissional. Você pode responder por texto ou áudio, como preferir. Vamos começar?';

const normalizeRecruitmentText = (value = '') =>
  value
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');

const resolveRecruitmentVideoKey = (item = {}) => {
  const idToVideo = recruitmentVideoMap?.idToVideo || {};
  const textToVideo = recruitmentVideoMap?.textToVideo || {};
  const candidates = [
    item?.video,
    item?.videoKey,
    item?.video_key,
    item?.clipId,
    item?.clip_id,
    item?.mediaKey,
    item?.media_key,
    item?.id,
  ];

  for (const candidate of candidates) {
    if (typeof candidate !== 'string') continue;
    const trimmed = candidate.trim();
    if (!trimmed) continue;
    if (/^rs_[a-z0-9_]+$/i.test(trimmed)) return trimmed;
    if (idToVideo[trimmed]) return idToVideo[trimmed];
  }

  const normalizedText = normalizeRecruitmentText(item?.text || '');
  if (normalizedText && textToVideo[normalizedText]) {
    return textToVideo[normalizedText];
  }

  return (item?.id || '').toString().trim() || '';
};

const buildInterviewDocRef = ({ company, interviewTypeKey, denuncia, entrevistaId }) =>
  doc(db, caminhoDaEntrevista({
    empresa: company,
    tipo: interviewTypeKey,
    caso: denuncia,
    entrevista: entrevistaId,
  }));

const fetchPesquisaScriptVersion = async ({ companyKey, interviewTypeKey, denunciaKey }) => {
  if (!companyKey || !interviewTypeKey || !denunciaKey) return '';
  const callable = httpsCallable(functions, 'getPesquisaScriptVersion');
  const { data } = await callable({ companyKey, interviewTypeKey, denunciaKey });
  return (data?.scriptVersion || '').toString().trim();
};

const fetchScriptDocument = async ({ companyKey, interviewTypeKey, scriptVersion }) => {
  if (!companyKey || !interviewTypeKey || !scriptVersion) return null;
  const callable = httpsCallable(functions, 'getInterviewScriptDocument');
  const { data } = await callable({ companyKey, interviewTypeKey, scriptVersion });
  const sourceCompany = (data?.sourceCompany || '').toString().trim();
  const scriptDoc = data?.scriptDoc || null;
  if (!sourceCompany || !scriptDoc) return null;

  const normalized = normalizeEiaScriptDocument(
    {
      ...scriptDoc,
      _scriptSourceCompany: sourceCompany,
    },
    {
      company: sourceCompany,
      interviewType: interviewTypeKey,
      version: scriptVersion,
    }
  );

  return {
    ...normalized.raw,
    _scriptSourceCompany: sourceCompany,
    schemaVersion: normalized.schemaVersion,
    script: normalized.script,
    flow: normalized.flow,
    questionsCount: normalized.questionsCount,
  };
};


const fetchGeneratedRoteiroScriptDocument = async ({
  companyKey,
  interviewTypeKey,
  denunciaKey,
  scriptVersion = RS_DEFAULT_SCRIPT_VERSION
}) => {
  if (!companyKey || !interviewTypeKey || !denunciaKey) return null;
  // RS1/RS2 (BACKLOG.md): a pagina publica de entrevista roda sem autenticacao
  // (signInAnonymously foi removido de proposito), entao um getDoc direto do client
  // no path canonico accounts/acc_{slug}/modules/grc/recrutamento_e_selecao/{processoId}
  // e bloqueado pelas regras do Firestore. Callable server-side (admin SDK) resolve
  // isso e ja faz canonico->legado internamente (ver getGeneratedRoteiroScriptDocument
  // em modules/eia/functions/index.js).
  const callable = httpsCallable(functions, 'getGeneratedRoteiroScriptDocument');
  const { data: response } = await callable({ companyKey, interviewTypeKey, denunciaKey, scriptVersion });
  const data = response?.scriptDoc;
  if (!data) return null;
  const roteiroQuestions = Array.isArray(data?.roteiro?.questions) ? data.roteiro.questions : [];
  if (!roteiroQuestions.length) return null;

  const normalizedQuestions = roteiroQuestions
    .slice()
    .sort((a, b) => Number(a?.order || 0) - Number(b?.order || 0))
    .map((item, index) => ({
      id: item?.id || `RS_Q_${index + 1}`,
      q: item?.text || '',
      text: item?.text || '',
      video: resolveRecruitmentVideoKey(item) || `RS_Q_${index + 1}`,
      block: item?.block || item?.category || 'technical',
      category: item?.category || item?.block || 'technical',
      time_limit_sec: Number(item?.time_limit_sec || 180),
      order: Number(item?.order || (index + 1)),
      competencies: Array.isArray(item?.competencies) ? item.competencies : [],
      family: item?.family || 'all',
      seniority: item?.seniority || 'any',
    }));

  return {
    _scriptSourceCompany: companyKey,
    script: {
      tipo: interviewTypeKey,
      version: scriptVersion || data.scriptVersion || RS_DEFAULT_SCRIPT_VERSION,
      intro: (data?.introText || '').trim() || RS_DEFAULT_INTRO_TEXT,
      fim:
        data?.finalMessage ||
        'Obrigada pela sua participação. Sua entrevista foi concluída e suas respostas foram registradas. Em breve, nosso time de recrutamento e seleção entrará em contato com os próximos passos.',
      questions: normalizedQuestions,
      avatars: [
        {
          id: 'elza',
          label: 'Elza',
          default: true,
          video: RS_DEFAULT_INTRO_CLIP,
          // Recrutamento usa a biblioteca de videos em "elza" no storage.
          // Mantemos o id "elza" no front (avatar padrao), mas com path explicito.
          path: `eia/media/${interviewTypeKey}/default/elza`,
          aliases: {
            intro: RS_DEFAULT_INTRO_CLIP,
          },
        }
      ],
      features: {
        avatarPicker: false
      }
    },
    flow: null,
    questionsCount: normalizedQuestions.length,
  };
};

const normalizeArrayLike = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') {
    return Object.entries(value)
      .sort(([a], [b]) => {
        const na = Number.parseInt(String(a).replace(/[^0-9]/g, ''), 10);
        const nb = Number.parseInt(String(b).replace(/[^0-9]/g, ''), 10);
        if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
        return String(a).localeCompare(String(b));
      })
      .map(([, item]) => item)
      .filter((item) => item !== undefined && item !== null);
  }
  return [];
};

const normalizeInterviewDocument = (raw = {}) => {
  const responses = normalizeArrayLike(raw.responses);
  const messages = normalizeArrayLike(raw.messages);
  const progress = normalizeArrayLike(raw.progress);

  return {
    ...raw,
    responses,
    messages,
    progress,
    inicioEntrevista: raw.inicioEntrevista ?? true,
    finalEntrevista: raw.finalEntrevista ?? false,
  };
};

const saveInterviewResults = async ({
  company,
  interviewTypeKey,
  denuncia,
  entrevistaId,
  data
}) => {
  const entrevistaDocRef = buildInterviewDocRef({
    company,
    interviewTypeKey,
    denuncia,
    entrevistaId
  });
  await setDoc(entrevistaDocRef, data, { merge: true });
  return entrevistaDocRef;
};

const saveInterviewResponses = async ({
  company,
  interviewTypeKey,
  denuncia,
  entrevistaId,
  responses,
  scriptVersion,
  workflowStatus,
  interactionPhase,
  statusModelVersion,
  accessToken = ''
}) => {
  if (accessToken) {
    const callable = httpsCallable(functions, 'saveInterviewResponsesByAccessToken');
    await callable({
      company,
      interviewTypeKey,
      denuncia,
      entrevistaId,
      token: accessToken,
      responses,
      scriptVersion,
      workflowStatus,
      interactionPhase,
      statusModelVersion
    });
    return null;
  }

  const statusPayload = buildStatusPayload({
    interviewTypeKey,
    workflowStatus,
    interactionPhase,
    statusModelVersion
  });

  return saveInterviewResults({
    company,
    interviewTypeKey,
    denuncia,
    entrevistaId,
    data: {
      responses,
      scriptVersion,
      ...statusPayload
    }
  });
};

// Write de conclusão (endInterview -> sendForm em ChatContainerEntrevista.js).
// A página pública roda sem Firebase Auth de propósito, então saveInterviewResults()
// (setDoc client-side direto) sempre batia em permission-denied pro candidato — só o
// autosave de progresso passava pelo token (saveInterviewResponses acima). Sem isso,
// a entrevista nunca era marcada como concluída mesmo com todas as respostas salvas.
const saveInterviewFinalResults = async ({
  company,
  interviewTypeKey,
  denuncia,
  entrevistaId,
  data,
  accessToken = ''
}) => {
  if (accessToken) {
    const callable = httpsCallable(functions, 'saveInterviewResponsesByAccessToken');
    await callable({
      company,
      interviewTypeKey,
      denuncia,
      entrevistaId,
      token: accessToken,
      responses: data.responses,
      scriptVersion: data.scriptVersion,
      workflowStatus: data.workflowStatus,
      interactionPhase: data.interactionPhase,
      statusModelVersion: data.statusModelVersion,
      inicioEntrevista: data.inicioEntrevista,
      finalEntrevista: data.finalEntrevista,
      sendToUserAt: data.sendToUserAt,
      sendToComplianceAt: data.sendToComplianceAt
    });
    return null;
  }

  return saveInterviewResults({
    company,
    interviewTypeKey,
    denuncia,
    entrevistaId,
    data
  });
};

// Write do formulário inicial (FormContainer.js -> chatUp). Mesmo racional de
// saveInterviewFinalResults acima: setDoc direto do client sempre falhava pro
// candidato não autenticado (path legado além de tudo) — as respostas de
// setor/cargo/tempoEmpresa/vinculoEmpregaticio (obrigatórias pro fluxo de
// compliance, STG6) nunca chegavam no Firestore de verdade.
const saveInterviewFormInicial = async ({
  company,
  interviewTypeKey,
  denuncia,
  entrevistaId,
  data,
  accessToken = ''
}) => {
  if (accessToken) {
    const callable = httpsCallable(functions, 'saveInterviewResponsesByAccessToken');
    await callable({
      company,
      interviewTypeKey,
      denuncia,
      entrevistaId,
      token: accessToken,
      ...data
    });
    return null;
  }

  return saveInterviewResults({
    company,
    interviewTypeKey,
    denuncia,
    entrevistaId,
    data
  });
};

const saveResumeTarget = async ({
  company,
  interviewTypeKey,
  denuncia,
  entrevistaId,
  resumeQuestionId,
  resumeQuestionIndex,
  accessToken = ''
}) => {
  if (accessToken) {
    const callable = httpsCallable(functions, 'saveInterviewResumeTargetByAccessToken');
    await callable({
      company,
      interviewTypeKey,
      denuncia,
      entrevistaId,
      token: accessToken,
      resumeQuestionId,
      resumeQuestionIndex
    });
    return;
  }
  return saveInterviewResults({
    company,
    interviewTypeKey,
    denuncia,
    entrevistaId,
    data: { resumeQuestionId, resumeQuestionIndex }
  });
};


const resetInterviewState = async ({
  company,
  interviewTypeKey,
  denuncia,
  entrevistaId,
  workflowStatus,
  interactionPhase,
  statusModelVersion
}) => {
  const statusPayload = buildStatusPayload({
    interviewTypeKey,
    workflowStatus,
    interactionPhase,
    statusModelVersion
  });

  return saveInterviewResults({
    company,
    interviewTypeKey,
    denuncia,
    entrevistaId,
    data: {
      responses: [],
      messages: [],
      progress: [],
      finalEntrevista: false,
      inicioEntrevista: true,
      resumeQuestionId: deleteField(),
      resumeQuestionIndex: deleteField(),
      finalizarMensagemExibida: deleteField(),
      finalizada: deleteField(),
      encerrada: deleteField(),
      finalizadoEm: deleteField(),
      ...statusPayload
    }
  });
};
const fetchInterviewDocument = async ({
  company,
  interviewTypeKey,
  denuncia,
  entrevistaId,
  accessToken = ''
}) => {
  if (!company || !interviewTypeKey || !denuncia || !entrevistaId) return null;
  if (accessToken) {
    const getInterviewByAccessToken = httpsCallable(functions, 'getInterviewByAccessToken');
    const { data } = await getInterviewByAccessToken({
      company,
      interviewTypeKey,
      denuncia,
      entrevistaId,
      token: accessToken
    });
    const interviewData = data?.interview || null;
    return interviewData ? normalizeInterviewDocument(interviewData) : null;
  }
  const entrevistaDocRef = buildInterviewDocRef({
    company,
    interviewTypeKey,
    denuncia,
    entrevistaId
  });
  const snap = await getDoc(entrevistaDocRef);
  if (!snap.exists()) return null;
  return normalizeInterviewDocument(snap.data() || {});
};

const fetchInterviewByAccessToken = async ({
  company,
  interviewTypeKey,
  denuncia,
  entrevistaId,
  token
}) => {
  if (!company || !interviewTypeKey || !denuncia || !entrevistaId || !token) return null;
  const callable = httpsCallable(functions, 'getInterviewByAccessToken');
  const { data } = await callable({
    company,
    interviewTypeKey,
    denuncia,
    entrevistaId,
    token
  });
  return data || null;
};

const fetchFormularioInicialConfig = async ({ company }) => {
  if (!company) return null;
  const callable = httpsCallable(functions, 'getFormularioInicialConfig');
  const { data } = await callable({ company });
  return data || null;
};

const fetchCompanyPublicLabel = async ({ company }) => {
  if (!company) return '';
  const callable = httpsCallable(functions, 'getCompanyPublicLabel');
  const { data } = await callable({ company });
  return (data?.companyLabel || '').toString().trim();
};

const sendCompletionEmails = async ({
  company,
  interviewType,
  numeroDenuncia,
  entrevistaId
}) => {
  const sendEmail = httpsCallable(functions, 'sendInterviewCompletionEmail');
  await sendEmail({ company, interviewType, numeroDenuncia, entrevistaId });
  const sendComplianceEmail = httpsCallable(functions, 'sendComplianceNotificationEmail');
  await sendComplianceEmail({ company, interviewType, numeroDenuncia, entrevistaId });
};

const requestEvidenceUploadUrls = async ({ company, denuncia, nome, files }) => {
  const getEvidenceUploadUrls = httpsCallable(functions, 'getEvidenceUploadUrls');
  const { data } = await getEvidenceUploadUrls({ company, denuncia, nome, files });
  return data;
};

export {
  fetchPesquisaScriptVersion,
  fetchScriptDocument,
  fetchInterviewDocument,
  fetchInterviewByAccessToken,
  fetchCompanyPublicLabel,
  saveInterviewResults,
  saveInterviewResponses,
  saveInterviewFinalResults,
  saveInterviewFormInicial,
  fetchFormularioInicialConfig,
  saveResumeTarget,
  resetInterviewState,
  sendCompletionEmails,
  requestEvidenceUploadUrls,
  fetchGeneratedRoteiroScriptDocument
};
